// components/details/TopMenu.tsx

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { ActionIcon, Menu, Image, Loader } from "@mantine/core";
import {
  IconMenu2,
  IconArrowsLeftRight,
  IconSettings,
  IconTrash,
  IconLockPlus,
  IconLogout,
  IconRefresh,
  IconCreditCard,
  IconBrandTiktok,
} from "@tabler/icons-react";
import { BobsWorkspace } from "../../stores/BobsWorkspace";
import {
  BOB_MODALS,
  BOB_PAGES,
  TIKTOK_SPONSORSHIPS_ENABLED,
} from "../../helpers/constants";
import { handleExit, handleReload } from "../../helpers/navigation";

import {
  getTopButtonStyle,
  getTopIconSize,
  TOP_BUTTON_VARIANT,
  TOP_BUTTON_RADIUS,
  TOP_BUTTON_SIZE,
} from "./buttons";

const TopMenu = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const selectedBob = bobsWorkspace.selectedBob;

    const [isMounted, setIsMounted] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [isReloading, setIsReloading] = useState(false);

    useEffect(() => {
      setIsMounted(true);
    }, []);

    const buttonStyle = getTopButtonStyle();
    const iconSize = getTopIconSize();

    const handleExitClick = () => {
      setIsExiting(true);
      handleExit();
    };

    const handleReloadClick = () => {
      setIsReloading(true);
      handleReload();
    };

    if (!isMounted) return null;

    return (
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <ActionIcon
            variant={TOP_BUTTON_VARIANT}
            size={TOP_BUTTON_SIZE}
            radius={TOP_BUTTON_RADIUS}
            aria-label="Menu"
            style={buttonStyle}
          >
            <IconMenu2 size={iconSize} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconSettings size={iconSize} />}
            onClick={() => selectedBob.setOpenModal(BOB_MODALS.SETTINGS)}
          >
            Settings
          </Menu.Item>

          <Menu.Item
            leftSection={
              <Image
                src="https://daxwktpz4hkwd.cloudfront.net/nwc_logos/nwc-square-color-two.png"
                alt="NWC Logo"
                w={iconSize}
                h={iconSize}
                fit="contain"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
            }
            onClick={() => selectedBob.setOpenModal(BOB_MODALS.NWC_ENTRY)}
          >
            NWC Codes
          </Menu.Item>

          {TIKTOK_SPONSORSHIPS_ENABLED && selectedBob.sponsorships.length > 0 && (
            <Menu.Item
              leftSection={<IconBrandTiktok size={iconSize} />}
              onClick={() => selectedBob.setOpenModal(BOB_MODALS.SPONSORSHIP)}
            >
              Sponsorship
            </Menu.Item>
          )}

          <Menu.Divider />

          {bobsWorkspace.bobs.length > 1 && (
            <Menu.Item
              leftSection={<IconArrowsLeftRight size={iconSize} />}
              onClick={() => selectedBob.setOpenModal(BOB_MODALS.SELECT_BOB)}
            >
              Switch Vault
            </Menu.Item>
          )}

          <Menu.Item
            leftSection={<IconLockPlus size={iconSize} />}
            onClick={() => selectedBob.setOpenModal(BOB_MODALS.CREATE_NEW_BOB)}
          >
            New Vault
          </Menu.Item>

          <Menu.Item
            leftSection={<IconCreditCard size={iconSize} />}
            onClick={() => {
              selectedBob.setGiftCardView(
                selectedBob.bobGifts.data.length > 0 ? "purchased" : "shop",
              );
              selectedBob.setOpenPage(BOB_PAGES.GIFT_CARD);
            }}
          >
            Gift Cards
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item
            leftSection={
              isReloading ? (
                <Loader size={iconSize} />
              ) : (
                <IconRefresh size={iconSize} />
              )
            }
            onClick={handleReloadClick}
            disabled={isReloading}
            closeMenuOnClick={false}
          >
            Reload App
          </Menu.Item>

          <Menu.Item
            leftSection={
              isExiting ? (
                <Loader size={iconSize} />
              ) : (
                <IconLogout size={iconSize} />
              )
            }
            onClick={handleExitClick}
            disabled={isExiting}
            closeMenuOnClick={false}
          >
            Exit
          </Menu.Item>

          <Menu.Divider />
          <Menu.Label>Danger zone</Menu.Label>
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={iconSize} />}
            onClick={() => selectedBob.setOpenModal(BOB_MODALS.DESTROY_WALLET)}
          >
            Delete Vault
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  },
);

export default TopMenu;
